import {default as classNames} from "classnames";
import {useEffect, useRef, useState} from "react";
import {CopyToClipboard} from "react-copy-to-clipboard";
import {FiCheck, FiCopy, FiEye, FiEyeOff} from "react-icons/fi";

import {Card} from "@/components/card/Card.tsx";
import {CardProps} from "@/components/card/Card.types.ts";
import {Icon} from "@/components/icon/Icon";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";

interface CodeBlockProps extends Omit<CardProps, "children"> {
  code: string;
  codeWithSecret?: string;
  codeClassName?: string;
  onModal?: boolean;
}

export const CodeBlock = ({code, codeWithSecret, onModal, className, codeClassName, ...props}: CodeBlockProps) => {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [showCopied, setShowCopied] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <Card {...props} className={classNames(className, "pulseTextBodySmall text-break")}>
      <CopyToClipboard
        text={codeWithSecret ?? code}
        onCopy={() => {
          setShowCopied(true);
          timerRef.current = setTimeout(() => {
            setShowCopied(false);
          }, 2000);
        }}
      >
        <div className="ms-1 float-end">
          <Tooltip tooltipContent={showCopied ? "Copied" : "Copy to Clipboard"} delayMs={500} onModal={onModal}>
            <Icon
              icon={showCopied ? FiCheck : FiCopy}
              className={classNames(showCopied ? "pulse-color-green-500" : "pulse-color-gray-500 cursor-pointer")}
              display="inline"
              removeDivDimensions
            />
          </Tooltip>
        </div>
      </CopyToClipboard>
      {codeWithSecret && (
        <div className="me-1 float-end">
          <IconButton
            className={showSecret ? "pulse-color-gray-500" : "pulse-color-blue-500 cursor-pointer"}
            icon={showSecret ? FiEyeOff : FiEye}
            onClick={() => setShowSecret((prev) => !prev)}
            size="fitIcon"
            iconProps={{display: "inline", removeDivDimensions: true}}
            variant="ghost"
          />
        </div>
      )}
      <code className={codeClassName}>{showSecret ? codeWithSecret : code}</code>
    </Card>
  );
};
